import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { BorrowerStateService, MlaBorrowerService } from '@ld.consumer-portal/data-access';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

const WIZARD_RESUME_KEY = 'wizard_resume_data';

interface WizardResumeData {
  loanId: string;
  borrowerId: string;
  section: string;
  page: string;
  timestamp: number;
}

export const wizardResumeGuard: CanActivateFn = async (route) => {
  const loggingService = inject(LoggingService);
  const borrowerStateService = inject(BorrowerStateService);
  const mlaBorrowerService = inject(MlaBorrowerService);

  const loanId = route.queryParamMap.get('loanId');

  if (!loanId) {
    clearResumeData();
    return true;
  }

  try {
    const borrowers = await mlaBorrowerService.getBorrowerInfo();
    const primaryBorrower = borrowers.find((b) => b.isPrimary);

    if (!primaryBorrower) {
      loggingService.logWarning('[WizardResumeGuard] No primary borrower found');
      return true;
    }

    const borrowerId = primaryBorrower.borrowerInfoId;

    const borrowerState = await firstValueFrom(borrowerStateService.getBorrowerState(loanId, borrowerId));

    if (borrowerState) {
      const resumeData: WizardResumeData = {
        loanId,
        borrowerId,
        section: String(borrowerState.WorkflowState ?? 0),
        page: '', // Will be determined by wizard service based on WorkflowState
        timestamp: Date.now(),
      };

      localStorage.setItem(WIZARD_RESUME_KEY, JSON.stringify(resumeData));

      if (borrowerState.MLAState) {
        localStorage.setItem(`wizard_form_data_${loanId}`, borrowerState.MLAState);
      }
    } else {
      const resumeData: WizardResumeData = {
        loanId,
        borrowerId,
        section: '0', // Unknown/start
        page: 'get-started',
        timestamp: Date.now(),
      };
      localStorage.setItem(WIZARD_RESUME_KEY, JSON.stringify(resumeData));
    }

    return true;
  } catch (error) {
    loggingService.logException(error, '[WizardResumeGuard] Error loading resume data:');
    return true;
  }
};

export function getResumeData(): WizardResumeData | null {
  try {
    const data = localStorage.getItem(WIZARD_RESUME_KEY);
    if (data) {
      const parsed = JSON.parse(data) as WizardResumeData;
      if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return parsed;
      }
      clearResumeData();
    }
  } catch {
    clearResumeData();
  }
  return null;
}

export function getStoredFormData(loanId: string): string | null {
  return localStorage.getItem(`wizard_form_data_${loanId}`);
}

export function clearResumeData(): void {
  localStorage.removeItem(WIZARD_RESUME_KEY);
}

export function clearStoredFormData(loanId: string): void {
  localStorage.removeItem(`wizard_form_data_${loanId}`);
}
